import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import API_BASE_URL from '../config';
import Toast from '../components/common/Toast';
import './CartPage.css';

const CartPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [toast, setToast] = useState(null);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/assets/product-placeholder.jpg';
    if (imagePath.startsWith('http')) return imagePath;
    return imagePath.replace('/assets/assets/', '/assets/');
  };

  const showToast = (message, type = 'success') => setToast({ message, type });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/cart', message: 'Please login to view your cart' } });
      return;
    }
    fetchCart();
  }, [isAuthenticated, navigate]);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/cart`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCartItems(data.data || []);
      } else {
        setCartItems([]);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    setUpdatingId(itemId);
    try {
      const response = await fetch(`${API_BASE_URL}/cart/update/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ quantity: newQuantity })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setCartItems(prev => prev.map(item => item.id === itemId ? { ...item, quantity: newQuantity } : item));
        showToast('Cart updated', 'success');
      } else {
        showToast(data.message || 'Failed to update quantity', 'error');
      }
    } catch (error) {
      showToast('Error updating quantity', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const removeItem = async (itemId) => {
    if (!window.confirm('Remove this item from cart?')) return;
    setUpdatingId(itemId);
    try {
      const response = await fetch(`${API_BASE_URL}/cart/remove/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setCartItems(prev => prev.filter(item => item.id !== itemId));
        showToast('Item removed from cart', 'success');
      } else {
        showToast(data.message || 'Failed to remove item', 'error');
      }
    } catch (error) {
      showToast('Error removing item', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  // Renders prescription info — handles both manual and image types
  const renderPrescription = (prescription) => {
    if (!prescription) return null;
    if (prescription.type === 'image') {
      return (
        <div className="item-prescription">
          <p>📷 Prescription: <a href={getImageUrl(prescription.image_url)} target="_blank" rel="noopener noreferrer" className="rx-image-link">View uploaded image</a></p>
        </div>
      );
    }
    return (
      <div className="item-prescription">
        <p>Prescription included</p>
        {prescription.right?.sph && <p>Right SPH: {prescription.right.sph} | Left SPH: {prescription.left?.sph || '-'}</p>}
      </div>
    );
  };

  const calculateSubtotal = () => cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  const formatPrice = (price) => `₹${parseFloat(price).toFixed(2)}`;

  if (loading) {
    return <div className="cart-page"><div className="container"><div className="loading-spinner">Loading cart...</div></div></div>;
  }

  return (
    <div className="cart-page">
      <div className="container">
        <h1 className="page-title">
          <span className="title-line"></span>
          <span className="title-line highlight"> Shopping Cart</span>
        </h1>

        {cartItems.length === 0 ? (
          <div className="empty-cart">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#95a5a6" strokeWidth="1.5">
              <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
            <h3>Your cart is empty</h3>
            <p>Looks like you haven't added any items yet.</p>
            <Link to="/products" className="btn-primary">Continue Shopping</Link>
          </div>
        ) : (
          <div className="cart-grid">
            <div className="cart-items">
              {cartItems.map(item => (
                <div key={item.id} className="cart-item">
                  <div className="item-image">
                    <img src={getImageUrl(item.image)} alt={item.product_name} onError={(e) => { e.target.src = '/assets/product-placeholder.jpg'; }} />
                  </div>
                  <div className="item-details">
                    <h3 className="item-title">{item.product_name}</h3>
                    {item.variant_name && <p className="item-variant">Color: {item.variant_name}</p>}
                    {item.size_name    && <p className="item-size">Size: {item.size_name}</p>}
                    {item.lens_name    && <p className="item-lens">Lens: {item.lens_name}{item.lens_range_name && ` - ${item.lens_range_name}`}</p>}
                    {item.lens_color_name && <p className="item-lens">Lens Color: {item.lens_color_name}</p>}
                    {renderPrescription(item.prescription)}
                  </div>
                  <div className="item-price"><span className="price">{formatPrice(item.price)}</span></div>
                  <div className="item-quantity">
                    <button onClick={() => updateQuantity(item.id, item.quantity - 1)} disabled={updatingId === item.id || item.quantity <= 1}>-</button>
                    <span>{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, item.quantity + 1)} disabled={updatingId === item.id}>+</button>
                  </div>
                  <div className="item-total"><span>{formatPrice(item.price * item.quantity)}</span></div>
                  <button className="item-remove" onClick={() => removeItem(item.id)} disabled={updatingId === item.id}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <div className="cart-summary">
              <h3>Order Summary</h3>
              <div className="summary-row"><span>Subtotal</span><span>{formatPrice(calculateSubtotal())}</span></div>
              <div className="summary-row total"><span>Total</span><span>{formatPrice(calculateSubtotal())}</span></div>
              <button className="checkout-btn" onClick={() => navigate('/checkout')}>Proceed to Checkout</button>
              <Link to="/products" className="continue-shopping">Continue Shopping</Link>
            </div>
          </div>
        )}
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default CartPage;